import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Check,
  Music,
  Users,
  Award,
  Sparkles,
  Shield,
  BookOpen,
  Heart,
  MapPin,
  Image as ImageIcon,
  GraduationCap,
} from "lucide-react";
import { Link } from "react-router-dom";

import erzieherinKinder from "@/assets/musik-kita/erzieherin-kinder.png";
import kinderInstrumente from "@/assets/musik-kita/kinder-instrumente.jpg";
import musikKitaTuerAsset from "@/assets/musik-kita/musik-kita-tuer.jpg.asset.json";
import musikKitaGruppeAsset from "@/assets/musik-kita/musik-kita-gruppe.jpg.asset.json";
import musikKitaElternAsset from "@/assets/musik-kita/musik-kita-eltern.jpg.asset.json";
import SEO from "@/components/SEO";
import { KURSE_KURZ, KURSE_TEXT, KURSE_ZAHL, LEKTIONEN_TEXT, gross } from "@/config/webseiteFakten";

const PAKET_ROUTE = "/musik-kita-paket";
const TRIAL_ROUTE = "/musik-kita-paket?trial=1";

// Dekorativer dritter Marken-Akzent (aus dem Gütesiegel-Badge übernommen) –
// wird nur sparsam für Deko-Blobs/Highlights genutzt, nicht für Text/UI.
const MAGENTA = "#D4145A";

const heroFeatures = [
  { icon: Users, text: "Zugang fürs ganze Team" },
  { icon: Music, text: `Live-Unterricht & ${KURSE_KURZ} Kurse` },
  { icon: Heart, text: "Eltern-Zugang inklusive" },
  { icon: Award, text: "Gütesiegel + Verzeichniseintrag" },
];

const warumBenefits = [
  {
    icon: Award,
    title: "Musik wird Teil eures Profils",
    description:
      "Als MusiKonzept Musik-Kita zeigt ihr nach außen sichtbar, dass Musik fest zu eurer pädagogischen Arbeit gehört – mit Siegel, Grafik und Eintrag im Verzeichnis.",
  },
  {
    icon: GraduationCap,
    title: "Euer Team wird sicherer",
    description:
      `${gross(KURSE_TEXT)} und ${LEKTIONEN_TEXT}, Live-Unterricht und Miko-KI geben eurem Team konkrete Ideen für den Alltag – ohne stundenlange Vorbereitung.`,
  },
  {
    icon: Heart,
    title: "Eltern werden eingebunden",
    description:
      "Eltern bekommen als Bonus einen eigenen Zugang zu Kinderliedern, Videos und Materialien – so entsteht eine Brücke zwischen Kita und Zuhause.",
  },
  {
    icon: Users,
    title: "Teil des Netzwerks",
    description:
      "Ihr werdet Teil des MusiKonzept Musik-Kita-Verzeichnisses und profitiert von der Sichtbarkeit für suchende Familien und Träger.",
  },
];

const paketFeatures = [
  `${gross(LEKTIONEN_TEXT)} in über ${KURSE_ZAHL} Kursen`,
  "Regelmäßiger Live-Unterricht mit Fachdozent:innen",
  "Eltern-Zugang inklusive – als Bonus für eure Familien",
  "Buchpaket im Wert von über 100 € für eure Einrichtung",
  "Anspruch auf Musik-Kita-Gütesiegel + Verzeichniseintrag",
  "Miko – KI-Assistent für musikpädagogische Ideen",
  "Community, Zertifikate & Teilnahmenachweise",
];

const siegelSteps = [
  {
    number: "01",
    title: "Paket buchen",
    description:
      "Ihr bucht das Musik-Kita-Paket für eure Einrichtung. Team- und Eltern-Zugang sind sofort freigeschaltet.",
  },
  {
    number: "02",
    title: "3 Online-Fortbildungen absolvieren",
    description:
      "Eine Person aus eurem Team absolviert in eigenem Tempo drei kurze Online-Fortbildungen:",
    kurse: [
      "Orff-Instrumente leicht lernen - Spiel Deinen Beat!",
      "Sandra Lierz - Meine Lieder für jeden Tag",
      "Webinar-Aufzeichnung \"Arme schütteln, Popo wackeln - Mitmach- & Bewegungslieder in Krippe & Kita\"",
    ],
  },
  {
    number: "03",
    title: "Siegel per Klick anfordern",
    description:
      "Sobald die drei Fortbildungen abgeschlossen sind, fordert ihr das Siegel mit einem Klick an – wir schicken Sticker, digitale Grafik & Buchpaket und tragen euch ins Verzeichnis ein.",
  },
];

const beweisKacheln = [
  {
    icon: Award,
    title: "Offizielles Siegel",
    description: "Aufkleber & Sticker für Eingangstür, Elternpost und Aushänge.",
  },
  {
    icon: ImageIcon,
    title: "Digitale Grafik",
    description: "Grafikdatei für Website, Social Media und Konzept-PDF.",
  },
  {
    icon: MapPin,
    title: "Verzeichniseintrag",
    description: "Sichtbarer Eintrag im MusiKonzept Musik-Kita-Verzeichnis.",
  },
];

const elternBonus = [
  {
    icon: BookOpen,
    title: "Vollzugriff auf die komplette Plattform",
    description:
      `Eltern nutzen dieselben Inhalte wie das Team: ${KURSE_TEXT} mit ${LEKTIONEN_TEXT}, Live-Unterricht und Miko-KI – ob Gitarre lernen, Lieder fürs Zuhause oder musikalische Impulse für den Alltag.`,
  },
  {
    icon: Heart,
    title: "Brücke zwischen Kita & Zuhause",
    description:
      "Was in der Kita begonnen wird, zieht sich zuhause fort. Eltern können mit den Kindern die Lieder nachsingen, Rhythmen üben oder einfach gemeinsam musizieren – ohne Zusatzkosten für die Familien.",
  },
];

const faqs = [
  {
    question: "Was kostet das Musik-Kita-Paket genau?",
    answer:
      "590 € pro Jahr für die ganze Einrichtung – alles inklusive. Team-Zugang, Eltern-Zugang, Live-Unterricht, Buchpaket, Anspruch auf das Siegel und Verzeichniseintrag. Keine versteckten Kosten, keine Zusatzgebühren.",
  },
  {
    question: "Gibt es ein kostenloses Kennenlernen?",
    answer:
      "Ja. Ihr könnt das Paket 2 Wochen kostenlos testen und in Ruhe schauen, ob es zu eurer Einrichtung passt.",
  },
  {
    question: "Wer darf das Paket buchen?",
    answer:
      "Kitas, Krippen und ähnliche Einrichtungen. Jede Kita kann direkt starten.",
  },
  {
    question: "Wie viele Team-Mitglieder können teilnehmen?",
    answer:
      "Das Paket ist für die ganze Einrichtung gedacht. Ihr könnt euer Team einladen, alle nutzen die Kurse und Materialien gemeinsam.",
  },
  {
    question: "Wie kommen wir konkret an das Siegel?",
    answer:
      "Eine Person aus eurem Team absolviert drei kurze Online-Fortbildungen. Danach fordert ihr das Siegel per Klick an – wir schicken Sticker, digitale Grafik und Buchpaket und nehmen euch ins Verzeichnis auf.",
  },
  {
    question: "Was, wenn wir gerade nicht dazu kommen, die Fortbildungen zu machen?",
    answer:
      "Kein Problem. Euer Zugang bleibt bestehen, das Team kann alle Inhalte weiter nutzen. Das Siegel könnt ihr jederzeit später freischalten – ohne Fristdruck.",
  },
  {
    question: "Können wir eine Rechnung an Kita oder Träger stellen lassen?",
    answer:
      "Ja. Wir stellen die Rechnung wahlweise direkt an die Kita oder an den Träger aus – auf Wunsch mit späterem Rechnungsdatum, passend zu eurem Haushaltsjahr.",
  },
];

// Kleiner handgezeichnet wirkender Unterstrich-Swash für Schlüsselwörter in Headlines.
const HeadlineSwash = ({ className = "" }: { className?: string }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 220 14"
    className={`absolute left-0 right-0 -bottom-2 w-full h-3 pointer-events-none ${className}`}
    preserveAspectRatio="none"
  >
    <path
      d="M2 9 C 40 2, 90 12, 130 6 S 200 4, 218 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

// Dekorative Musik-Note für Hintergrund-Deko.
const DecoNote = ({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <Music
    aria-hidden="true"
    className={`absolute pointer-events-none ${className ?? ""}`}
    style={style}
  />
);

const MusikKita = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      <SEO
        title="Musik-Kita-Paket – Fortbildungen, Live-Unterricht & Gütesiegel | MusiKonzept"
        description="Werdet MusiKonzept Musik-Kita: 590 €/Jahr für Team-Zugang, Live-Unterricht, Eltern-Zugang, Buchpaket und Anspruch auf das Musik-Kita-Gütesiegel."
        path="/musik-kita"
      />
      <Header />

      {/* Hero – gestaltetes Poster */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        {/* Hintergrund-Komposition: Blobs in Marken-Magenta/Teal/Gold */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div
          className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        />
        <div
          className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
          style={{ background: `radial-gradient(circle, ${MAGENTA}55, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full blur-3xl opacity-40"
          style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.35), transparent 70%)" }}
        />
        {/* Verstreute, tief transparente Noten */}
        <DecoNote className="w-10 h-10 text-primary/15 top-24 left-[8%] rotate-[-12deg]" />
        <DecoNote className="w-8 h-8 text-accent/25 top-40 right-[12%] rotate-[18deg]" />
        <DecoNote className="w-14 h-14 top-[55%] left-[4%] rotate-[8deg]" style={{ color: `${MAGENTA}30` }} />
        <DecoNote className="w-6 h-6 text-primary/20 bottom-24 right-[30%] rotate-[-20deg]" />

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Text-Spalte */}
            <div className="lg:col-span-5 space-y-8 animate-fade-in">
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                Für Kitas & Einrichtungen
              </Badge>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                Werdet{" "}
                <span className="relative inline-block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">
                    MusiKonzept Musik-Kita
                  </span>
                  <HeadlineSwash className="text-accent" />
                </span>
              </h1>

              <p className="text-xl text-muted-foreground leading-relaxed">
                Live-Unterricht, {KURSE_TEXT} mit {LEKTIONEN_TEXT},
                Eltern-Zugang und der Weg zum Musik-Kita-Gütesiegel. Ein Paket für
                die ganze Einrichtung.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {heroFeatures.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-sm">
                      <item.icon className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <span className="text-sm text-foreground pt-1">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  asChild
                  size="lg"
                  className="shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Link to={PAKET_ROUTE}>Musik-Kita-Paket buchen</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <Link to={TRIAL_ROUTE}>2 Wochen kostenlos testen</Link>
                </Button>
              </div>
            </div>

            {/* Bild-Spalte: Kita-Tür mit Siegel als Poster */}
            <div
              className="lg:col-span-7 relative animate-fade-in"
              style={{ animationDelay: "150ms" }}
            >
              {/* Layered Blobs hinter dem Bild */}
              <div
                className="absolute -top-6 -left-6 w-40 h-40 rounded-full blur-3xl opacity-70"
                style={{ background: `radial-gradient(circle, ${MAGENTA}66, transparent 70%)` }}
              />
              <div
                className="absolute -bottom-8 -right-8 w-56 h-56 rounded-full blur-3xl opacity-60"
                style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.4), transparent 70%)" }}
              />

              {/* Bild-Frame mit leichter Neigung */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-border/40 rotate-[-1.5deg] hover:rotate-0 transition-transform duration-500">
                <img
                  src={musikKitaTuerAsset.url}
                  alt="MusiKonzept Musik-Kita Gütesiegel an der Eingangstür einer Kita"
                  className="w-full h-[320px] md:h-[440px] lg:h-[520px] object-cover"
                  loading="eager"
                />
                {/* Sanfter Gradient unten für Kontrast auf dem Chip */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />

                {/* Floating Bild-Unterschrift */}
                <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 shadow-lg">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">
                      So sieht es bei einer Musik-Kita aus
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Warum */}
      <section className="py-20 px-4 bg-gradient-to-b from-muted/20 to-background relative overflow-hidden">
        <DecoNote className="w-24 h-24 text-primary/5 top-10 right-[5%] rotate-12" />
        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-16 space-y-4">
            <Badge className="bg-primary/10 text-primary border-primary/20">Vorteile</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Warum{" "}
              <span className="relative inline-block">
                <span className="text-primary">Musik-Kita</span>
                <HeadlineSwash className="text-primary/60" />
              </span>{" "}
              werden?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Sichtbar, alltagstauglich, verbindend – für Team, Kinder und Eltern.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            <div className="order-2 lg:order-1 relative">
              <div
                className="absolute -inset-4 rounded-[2rem] blur-2xl opacity-40 -z-10"
                style={{ background: `linear-gradient(135deg, hsl(var(--primary) / 0.3), ${MAGENTA}33)` }}
              />
                <img
                  src={musikKitaGruppeAsset.url}
                  alt="Kinder und Erzieherin musizieren gemeinsam in der Kita"
                  className="w-full h-auto max-h-[640px] object-cover rounded-3xl shadow-2xl"
                />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              {warumBenefits.map((benefit, index) => {
                const isNetzwerk = benefit.title === "Teil des Netzwerks";
                const cardContent = (
                  <Card
                    className="group relative overflow-hidden p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 border-border/50 bg-card/70 backdrop-blur-sm rounded-2xl"
                  >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-70" />
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                        <benefit.icon className="w-6 h-6 text-primary-foreground" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                        <p className="text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  </Card>
                );
                return isNetzwerk ? (
                  <Link key={index} to="/musik-kita-verzeichnis" className="block">
                    {cardContent}
                  </Link>
                ) : (
                  <div key={index}>{cardContent}</div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Paket */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div
          className="absolute top-1/2 -translate-y-1/2 -left-40 w-[400px] h-[400px] rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.4), transparent 70%)" }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -right-40 w-[400px] h-[400px] rounded-full blur-3xl opacity-30"
          style={{ background: `radial-gradient(circle, ${MAGENTA}55, transparent 70%)` }}
        />
        <div className="container mx-auto max-w-6xl relative">
          <div className="text-center mb-12 space-y-4">
            <Badge className="bg-accent/10 text-accent border-accent/20">Das Paket</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Das Musik-Kita-{" "}
              <span className="relative inline-block">
                <span className="text-accent">Paket</span>
                <HeadlineSwash className="text-accent/70" />
              </span>{" "}
              – alles drin
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Ein Paket. Ein Preis. Alles, was ihr für die musikalische Arbeit braucht.
            </p>
          </div>

          <Card className="overflow-hidden border-primary/20 shadow-2xl rounded-3xl">
            <div className="grid lg:grid-cols-2">
              {/* Links: Preis & CTAs */}
              <div className="relative p-8 md:p-12 bg-gradient-to-br from-primary/15 via-accent/10 to-transparent flex flex-col justify-between gap-8 overflow-hidden">
                <div
                  className="absolute -top-16 -right-16 w-64 h-64 rounded-full blur-2xl opacity-40"
                  style={{ background: `radial-gradient(circle, ${MAGENTA}66, transparent 70%)` }}
                />
                <div className="relative space-y-6">
                  <h3 className="text-2xl md:text-3xl font-bold leading-tight">
                    Ein Paket. Alles für die musikalische Kita.
                  </h3>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
                        590 €
                      </span>
                      <span className="text-xl text-muted-foreground">/ Jahr</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      für die ganze Einrichtung · alles inklusive
                    </p>
                  </div>
                </div>

                <div className="relative space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg" className="flex-1 shadow-lg">
                      <Link to={PAKET_ROUTE}>Musik-Kita-Paket buchen</Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="flex-1">
                      <Link to={TRIAL_ROUTE}>2 Wochen kostenlos testen</Link>
                    </Button>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      Keine versteckten Kosten
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      Rechnung an Kita bzw. Träger möglich
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      2 Wochen kostenlos testen
                    </li>
                  </ul>
                </div>
              </div>

              {/* Rechts: Features */}
              <div className="p-8 md:p-12 bg-card">
                <h4 className="font-bold text-lg mb-6">Was ist enthalten?</h4>
                <ul className="space-y-4">
                  {paketFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center mt-0.5 shadow-sm">
                        <Check className="w-4 h-4 text-primary-foreground" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Siegel-Mechanik – kräftiger Farb-Moment */}
      <section
        className="py-24 px-4 relative overflow-hidden text-primary-foreground"
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 40%, hsl(var(--accent)) 100%)",
        }}
      >
        {/* Deko */}
        <div
          className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-40"
          style={{ background: `radial-gradient(circle, ${MAGENTA}, transparent 65%)` }}
        />
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" />
        <DecoNote className="w-16 h-16 text-white/10 top-16 right-[8%] rotate-12" />
        <DecoNote className="w-10 h-10 text-white/10 bottom-24 left-[10%] rotate-[-15deg]" />

        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-14 space-y-4">
            <Badge className="bg-white/15 text-white border-white/25 hover:bg-white/20 backdrop-blur-sm">
              Gütesiegel
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              So bekommt ihr das{" "}
              <span className="relative inline-block">
                <span>Siegel</span>
                <HeadlineSwash className="text-white/70" />
              </span>
            </h2>
            <p className="text-xl text-white/85 max-w-3xl mx-auto">
              In drei einfachen Schritten – ihr verdient das Siegel einfach durch Nutzung.
            </p>
          </div>

          {/* 3-Schritte mit verbindender Linie */}
          <div className="relative mb-16">
            {/* Gepunktete Verbindungslinie (Desktop) */}
            <svg
              aria-hidden="true"
              className="hidden md:block absolute top-8 left-[16%] right-[16%] h-6 pointer-events-none"
              viewBox="0 0 1000 24"
              preserveAspectRatio="none"
            >
              <path
                d="M 0 12 Q 250 -8 500 12 T 1000 12"
                fill="none"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="2"
                strokeDasharray="2 8"
                strokeLinecap="round"
              />
            </svg>

            <div className="grid md:grid-cols-3 gap-6">
              {siegelSteps.map((step, index) => (
                <div
                  key={index}
                  className="relative animate-fade-in"
                  style={{ animationDelay: `${index * 120}ms` }}
                >
                  {/* Nummer-Badge: markante hexartige runde Form */}
                  <div className="relative mx-auto mb-5 w-16 h-16">
                    <div
                      className="absolute inset-0 rounded-[35%] bg-white shadow-xl rotate-45"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-primary to-accent">
                        {step.number}
                      </span>
                    </div>
                  </div>
                  <Card className="p-6 rounded-2xl border-0 bg-white/95 text-foreground shadow-2xl backdrop-blur">
                    <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                    {step.kurse && (
                      <ul className="mt-3 space-y-1.5">
                        {step.kurse.map((kurs, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-sm text-foreground"
                          >
                            <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>{kurs}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Beweis-Kacheln */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-center mb-8">
              Greifbarer Beweis für eure Einrichtung
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {beweisKacheln.map((item, index) => (
                <Card
                  key={index}
                  className="group p-6 text-center rounded-2xl border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-white/95 flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-105 transition-transform">
                    <item.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h4 className="font-bold text-lg mb-2 text-white">{item.title}</h4>
                  <p className="text-white/80 text-sm">{item.description}</p>
                </Card>
              ))}
            </div>
          </div>

          <Card className="p-6 rounded-2xl border-0 bg-white/10 backdrop-blur-sm max-w-3xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/95 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <p className="text-sm text-white/90 leading-relaxed">
                <strong className="text-white">Ihr kommt gerade nicht dazu?</strong>{" "}
                Kein Problem – euer Zugang bleibt bestehen, das Team kann alle Inhalte
                weiter nutzen. Das Siegel könnt ihr jederzeit später freischalten.
              </p>
            </div>
          </Card>

          {/* Sub-Beruhigung */}
          <div className="max-w-3xl mx-auto mt-6">
            <div className="flex items-center justify-center gap-3 text-white/80 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Kein Prüfungstermin. Kein Fristdruck. Ihr entscheidet, wann.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Eltern-Zugang als Bonus */}
      <section className="py-20 px-4 relative overflow-hidden">
        <DecoNote className="w-20 h-20 text-accent/10 bottom-10 left-[6%] rotate-[-8deg]" />
        <div className="container mx-auto max-w-7xl relative">
          <div className="text-center mb-16 space-y-4">
            <Badge className="bg-primary/10 text-primary border-primary/20">Bonus</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Eltern-Zugang inklusive – ein Mehrwert für die ganze Einrichtung
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Mit dem Paket erhalten auch die Eltern eurer Kita einen vollwertigen
              Zugang zur Plattform – als echter Mehrwert für die ganze Einrichtung.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {elternBonus.map((item, index) => (
                <Card
                  key={index}
                  className="group relative overflow-hidden p-6 rounded-2xl hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 border-border/50 bg-card/70 backdrop-blur-sm"
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ background: `linear-gradient(90deg, hsl(var(--primary)), ${MAGENTA})` }}
                  />
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                      <item.icon className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                      <p className="text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="relative">
              <div
                className="absolute -inset-4 rounded-[2rem] blur-2xl opacity-40 -z-10"
                style={{ background: `linear-gradient(135deg, hsl(var(--accent) / 0.35), ${MAGENTA}33)` }}
              />
              <img
                src={musikKitaElternAsset.url}
                alt="Familie musiziert gemeinsam zu Hause mit Gitarre und Tamburin"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-gradient-to-b from-muted/20 to-background">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12 space-y-4">
            <Badge className="bg-primary/10 text-primary border-primary/20">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Häufige Fragen
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border border-border/50 rounded-2xl px-6 bg-card/70 backdrop-blur-sm data-[state=open]:border-primary/40 data-[state=open]:shadow-md transition-all"
              >
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Abschluss-CTA */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <Card className="relative overflow-hidden p-10 md:p-16 text-center border-primary/20 rounded-3xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10">
            <div
              className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full blur-3xl opacity-40"
              style={{ background: `radial-gradient(circle, ${MAGENTA}55, transparent 70%)` }}
            />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_theme(colors.primary/10),_transparent_70%)]" />
            <DecoNote className="w-16 h-16 text-primary/10 top-6 left-8 rotate-[-12deg]" />
            <DecoNote className="w-12 h-12 text-accent/20 bottom-8 right-10 rotate-12" />
            <div className="relative space-y-6">
              <img
                src={erzieherinKinder}
                alt=""
                aria-hidden="true"
                className="hidden"
              />
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
                Werdet jetzt{" "}
                <span className="relative inline-block">
                  <span className="text-primary">MusiKonzept</span>
                  <HeadlineSwash className="text-accent" />
                </span>{" "}
                Musik-Kita
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Ein Paket, ein Preis, alles drin – inklusive Weg zum Gütesiegel.
              </p>
              <div className="flex flex-wrap justify-center gap-4 pt-4">
                <Button asChild size="lg" className="shadow-lg">
                  <Link to={PAKET_ROUTE}>Musik-Kita-Paket buchen</Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to={TRIAL_ROUTE}>2 Wochen kostenlos testen</Link>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground pt-6">
                Ihr habt eine Musikschule oder andere Einrichtung?{" "}
                <Link to="/kontakt" className="text-primary hover:underline font-medium">
                  Sprecht uns an.
                </Link>
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MusikKita;
